import { Link, useLocation } from 'react-router-dom';

import { ArrowLeft, Shield } from 'lucide-react';

export function LegalLayout() {
  const location = useLocation();
  const isPrivacy = location.pathname.endsWith('privacidade');

  return (
    <div className="min-h-screen bg-surface flex flex-col justify-between">
      {/* Header Legal */}
      <header className="bg-card border-b border-border/80 h-16 flex items-center px-6 relative z-10 shadow-sm">
        <div className="w-full max-w-4xl mx-auto flex justify-between items-center">
          <Link to="/login" className="flex items-center space-x-2 text-text-muted hover:text-primary transition-colors text-sm font-semibold">
            <ArrowLeft className="h-4 w-4" />
            <span>Voltar ao login</span>
          </Link>
          <div className="flex items-center space-x-2 text-primary font-display font-bold text-sm">
            <Shield className="h-5 w-5 text-accent" />
            <span>Lumenise Legal</span>
          </div>
        </div>
      </header>

      {/* Container Principal */}
      <main className="flex-grow w-full max-w-4xl mx-auto px-6 py-10 lg:py-16">
        <div className="bg-card border border-border/60 rounded-2xl p-6 md:p-10 shadow-sm space-y-8">
          {/* Abas */}
          <div className="flex border-b border-border/85 pb-4 gap-4">
            <Link
              to="/legal/privacidade"
              className={`pb-2 text-sm font-bold border-b-2 transition-all ${isPrivacy ? 'border-accent text-accent' : 'border-transparent text-text-muted hover:text-primary'}`}
            >
              Política de Privacidade
            </Link>
            <Link
              to="/legal/termos"
              className={`pb-2 text-sm font-bold border-b-2 transition-all ${!isPrivacy ? 'border-accent text-accent' : 'border-transparent text-text-muted hover:text-primary'}`}
            >
              Termos de Uso
            </Link>
          </div>

          {/* Renderização das páginas internas */}
          <div className="prose max-w-none text-sm text-text leading-relaxed font-body">
            {isPrivacy ? <PrivacyContent /> : <TermsContent />}
          </div>
        </div>
      </main>

      {/* Footer Legal */}
      <footer className="bg-card border-t border-border/85 py-6 text-center text-xs text-text-muted">
        <p>© {new Date().getFullYear()} Lumenise Base. Todos os direitos reservados. Em conformidade com a LGPD.</p>
      </footer>
    </div>
  );
}

// Conteúdo resumido e elegante da Política de Privacidade
function PrivacyContent() {
  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl font-extrabold text-primary">Política de Privacidade</h1>
      <p className="font-mono text-xs text-text-muted">Última atualização: Maio de 2026</p>
      
      <p>
        Esta Política de Privacidade descreve como a Lumenise Base coleta, utiliza, processa e compartilha suas informações pessoais ao utilizar nossos serviços e nossa plataforma.
      </p>

      <h2 className="font-display text-lg font-bold text-primary pt-2">1. Coleta de Informações</h2>
      <p>
        Coletamos as informações necessárias para prestar nossos serviços de forma robusta e segura:
      </p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li><strong>Informações de Perfil:</strong> Nome completo, endereço de e-mail e avatar.</li>
        <li><strong>Logs de Atividade:</strong> Registro de data/hora, endereço IP, tipo de navegador e ações realizadas no painel (para auditorias de segurança).</li>
        <li><strong>Preferências de Visualização:</strong> Configurações de tema, acessibilidade e notificações de uso.</li>
      </ul>

      <h2 className="font-display text-lg font-bold text-primary pt-2">2. Uso dos Dados</h2>
      <p>
        Os dados pessoais coletados são utilizados estritamente para fins legítimos e necessários:
      </p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Prestação dos serviços contratados (Execução de Contrato — Art. 7º, V da LGPD).</li>
        <li>Garantia de segurança da plataforma, detecção de fraudes e prevenção a abusos (Legítimo Interesse).</li>
        <li>Histórico de auditoria interna exigido legalmente por entidades reguladoras (Obrigação Legal).</li>
      </ul>

      <h2 className="font-display text-lg font-bold text-primary pt-2">3. Compartilhamento e Sub-operadores</h2>
      <p>
        Para garantir o correto funcionamento da infraestrutura em nuvem, os dados podem ser armazenados e processados por sub-operadores de tecnologia de primeira linha que seguem os mais altos padrões de segurança, tais como:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Supabase Inc.</strong> (Banco de dados e autenticação)</li>
        <li><strong>Cloudflare Inc.</strong> (Hospedagem na borda e segurança anti-bot)</li>
        <li><strong>Google Cloud</strong> (OAuth de Autenticação e APIs de Inteligência)</li>
      </ul>

      <h2 className="font-display text-lg font-bold text-primary pt-2">4. Seus Direitos (Art. 18 LGPD)</h2>
      <p>
        Como titular dos dados, você pode exercer diretamente através de seu painel de Perfil os seguintes direitos:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Confirmação da existência de tratamento de dados pessoais.</li>
        <li>Acesso facilitado aos seus dados (Portabilidade via exportação JSON/ZIP).</li>
        <li>Revogação de consentimentos opcionais a qualquer momento.</li>
        <li>Solicitação de eliminação de dados tratados sob seu consentimento.</li>
      </ul>
    </div>
  );
}

// Conteúdo resumido e elegante dos Termos de Uso
function TermsContent() {
  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl font-extrabold text-primary">Termos de Uso</h1>
      <p className="font-mono text-xs text-text-muted">Última atualização: Maio de 2026</p>

      <p>
        Bem-vindo ao Lumenise Base. Ao acessar ou utilizar nossa plataforma administrativa, você aceita e concorda integralmente em cumprir estes Termos de Uso.
      </p>

      <h2 className="font-display text-lg font-bold text-primary pt-2">1. Elegibilidade e Cadastro</h2>
      <p>
        O Lumenise Base é uma plataforma de gestão e automação multi-tenant de caráter privado. O acesso à plataforma é restrito a usuários previamente convidados por gestores de tenants ativos. É de responsabilidade do usuário garantir a confidencialidade de suas credenciais de acesso OAuth.
      </p>

      <h2 className="font-display text-lg font-bold text-primary pt-2">2. Uso Aceitável</h2>
      <p>
        Ao usar a plataforma, você se compromete a:
      </p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Não tentar burlar os mecanismos de segurança ou as diretivas de RLS (Row Level Security) que separam os dados de cada tenant.</li>
        <li>Não realizar engenharia reversa das APIs internas da plataforma.</li>
        <li>Não abusar dos endpoints da API, sob risco de banimento automático por taxa excessiva (Rate Limiting).</li>
        <li>Respeitar a finalidade legítima de tratamento de dados pessoais coletados de terceiros sob a legislação eleitoral e civil brasileira vigente.</li>
      </ul>

      <h2 className="font-display text-lg font-bold text-primary pt-2">3. Limitação de Responsabilidade</h2>
      <p>
        A plataforma é fornecida "como está" e "conforme disponível". A Lumenise Base não se responsabiliza por prejuízos decorrentes de falhas de conectividade de rede externa, uso indevido de credenciais administrativas por terceiros ou atuações ilícitas por parte de seus usuários na coleta de dados.
      </p>

      <h2 className="font-display text-lg font-bold text-primary pt-2">4. Alterações e Rescisão</h2>
      <p>
        Reservamo-nos o direito de alterar estes termos ou suspender acessos preventivamente caso sejam detectados desvios de conduta ou suspeitas de brechas de segurança de dados pessoais, notificando o titular conforme os prazos legais da ANPD.
      </p>
    </div>
  );
}
